/**
 * Global blocks data layer — the shared-section library ("Blocks" page,
 * plus the Pages editor's palette group). Thin fetch wrapper over
 * /api/admin/global-blocks; no autosave here, Blocks edits save on an
 * explicit button (they fan out to every page using the block, so the
 * commit should be deliberate).
 */

import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from './config';
import { authHeaders } from './auth';

export interface GlobalBlockInfo {
  id: string;
  name: string;
  section: Record<string, unknown>;
  updated_at: string;
  usedBy: string[];
}

async function parseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error || `HTTP ${res.status}`;
}

export function useGlobalBlocks() {
  const [blocks, setBlocks] = useState<GlobalBlockInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/global-blocks`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error(await parseError(res));
      const data = (await res.json()) as { blocks: GlobalBlockInfo[] };
      setBlocks(data.blocks);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (name: string, section: Record<string, unknown>): Promise<{ ok: boolean; id?: string; error?: string }> => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/global-blocks`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ name, section }),
        });
        if (!res.ok) throw new Error(await parseError(res));
        const data = (await res.json()) as { block: GlobalBlockInfo };
        setBlocks((prev) => [data.block, ...prev]);
        return { ok: true, id: data.block.id };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [],
  );

  const update = useCallback(
    async (
      id: string,
      patch: { name?: string; section?: Record<string, unknown> },
    ): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/global-blocks/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(patch),
        });
        if (!res.ok) throw new Error(await parseError(res));
        const data = (await res.json()) as { block: GlobalBlockInfo };
        setBlocks((prev) => prev.map((b) => (b.id === id ? data.block : b)));
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    [],
  );

  const remove = useCallback(async (id: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/global-blocks/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(await parseError(res));
      setBlocks((prev) => prev.filter((b) => b.id !== id));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }, []);

  return { blocks, loading, error, reload, create, update, remove };
}
